import { useCallback, useEffect, useRef, useState, type FC } from 'react';
import SV from 'server-vars';
import { getPublisherTagInstance } from '../utils/getPublisherTagInstance';
import { loadGpt } from '../utils/gptLoader';
import VisibilityTracker from 'dibs-visibility-tracker/exports/VisibilityTracker';
import { type AdSizes, type SlotRenderEndedEvent } from '../types';
import { type OnAbsentAd } from '../hooks/useAbsentAdHandler';

type Props = {
    adId: string;
    elementId: string;
    width: number;
    height: number;
    sizes?: AdSizes;
    heightHold?: number;
    widthHold?: number;
    className?: string;
    onSlotRenderEnded?: (event: SlotRenderEndedEvent, element: HTMLDivElement) => void;
    onAbsentAd?: OnAbsentAd;
    lazyLoad?: boolean;
    isAbsentAd?: boolean;
};

export const AdUnit: FC<Props> = ({
    adId,
    elementId,
    width,
    height,
    className,
    onSlotRenderEnded,
    onAbsentAd,
    lazyLoad = false,
    sizes,
    heightHold,
    widthHold,
    isAbsentAd = false,
}) => {
    const adRef = useRef<HTMLDivElement>(null);

    const [gptLoaded, setGptLoaded] = useState(false);
    const [adSlot, setAdSlot] = useState<googletag.Slot | null>(null);

    const triggerGptLoad = useCallback(async (): Promise<void> => {
        await loadGpt();
        setGptLoaded(true);
    }, []);

    useEffect(() => {
        return () => {
            if (adSlot) {
                const gt = getPublisherTagInstance();
                gt?.destroySlots([adSlot]);
            }
        };
    }, [adSlot]);

    useEffect(() => {
        if (!lazyLoad && SV.get('isSbAdsEnabled')) {
            triggerGptLoad();
        }
    }, [lazyLoad, triggerGptLoad]);

    useEffect(() => {
        const gt = getPublisherTagInstance();
        const onSlotRenderEndedHandler = (event: SlotRenderEndedEvent): void => {
            if (adRef.current) {
                onSlotRenderEnded?.(event, adRef.current);
                if (event.isEmpty) {
                    onAbsentAd?.(event.slot.getSlotElementId());
                }
            }
        };
        if (onSlotRenderEnded || onAbsentAd) {
            gt?.pubads().addEventListener('slotRenderEnded', onSlotRenderEndedHandler);
        }
        if (gptLoaded && !adSlot) {
            gt?.cmd.push(() => {
                gt?.pubads();

                // define ad slot
                const slot = gt.defineSlot(adId, sizes ? sizes : [width, height], elementId);
                if (slot) {
                    // configure ad slot
                    slot.addService(gt.pubads());
                }

                if (!gt.pubadsReady) {
                    gt.pubads().enableSingleRequest();
                    gt.enableServices();
                }

                setAdSlot(slot);
            });

            gt?.cmd.push(() => {
                gt?.display(elementId);
            });
        }
        return () => {
            if (onSlotRenderEnded || onAbsentAd) {
                gt?.pubads().removeEventListener('slotRenderEnded', onSlotRenderEndedHandler);
            }
        };
    }, [gptLoaded, adSlot, onSlotRenderEnded, adId, sizes, width, height, elementId, onAbsentAd]);

    return (
        <>
            {!!lazyLoad && (
                <VisibilityTracker
                    onVisibilityChange={({ isVisible }) => {
                        if (isVisible && SV.get('isSbAdsEnabled')) {
                            triggerGptLoad();
                        }
                    }}
                    elementRef={adRef}
                    observerOptions={{
                        rootMargin: `200% 0px`, // Render slots within 2 viewports.
                    }}
                />
            )}
            <div
                style={isAbsentAd ? {} : { minHeight: heightHold || height }}
                className={className}
                id={elementId}
                data-tn="ad-unit"
                data-target-width={widthHold || width}
                data-target-height={heightHold || height}
                ref={adRef}
            />
        </>
    );
};
